export const STRUCTURED_SETUP_ITEM_TYPE = "structured_setup" as const;
export const STRUCTURED_SETUP_KIND = "structured_setup" as const;
export const STRUCTURED_SETUP_WORKFLOW_EDITOR_HREF = "/sales/automation/new" as const;

export type PageChatStructuredSetupStatus =
  | "applied"
  | "draft"
  | "failed"
  | "handoff_required"
  | "pending_approval"
  | "ready_for_review";

export type PageChatStructuredSetupActionKind = "apply" | "handoff" | "link" | "open_editor" | "review";

export interface PageChatStructuredSetupAction {
  id: string;
  label: string;
  status?: PageChatStructuredSetupStatus | string;
  kind?: PageChatStructuredSetupActionKind | string;
  description?: string;
  href?: string;
}

export interface PageChatStructuredSetupSection {
  title: string;
  body?: string;
  items?: string[];
}

export interface PageChatStructuredSetupProvenance {
  source?: string;
  generated_at?: string;
  generated_by?: string;
  references?: string[];
}

export interface PageChatStructuredSetupPayload {
  kind?: typeof STRUCTURED_SETUP_KIND;
  item_type?: typeof STRUCTURED_SETUP_ITEM_TYPE | string;
  version?: string;
  domain?: string;
  title?: string;
  intent_summary?: string;
  recommended_action?: string;
  risk_tier?: string;
  status?: PageChatStructuredSetupStatus | string;
  prerequisites?: string[];
  sections?: PageChatStructuredSetupSection[];
  structured_payload?: Record<string, any>;
  provenance?: PageChatStructuredSetupProvenance;
  allowed_actions?: PageChatStructuredSetupAction[];
  summary?: string;
  message?: string;
  source?: string;
}

const toText = (value: unknown, fallback = "") => {
  if (value === null || value === undefined) return fallback;
  const text = String(value).trim();
  return text || fallback;
};

const isPlainObject = (value: unknown): value is Record<string, any> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const normalizeActionHref = (value: unknown) => {
  const href = toText(value);
  return href.startsWith("/") && !href.startsWith("//") ? href : "";
};

export const normalizeStructuredSetupStatus = (value: unknown): PageChatStructuredSetupStatus => {
  const normalized = toText(value, "draft")
    .toLowerCase()
    .replace(/[-\s]+/g, "_");
  const aliases: Record<string, PageChatStructuredSetupStatus> = {
    apply: "applied",
    approve: "pending_approval",
    approved: "pending_approval",
    handoff: "handoff_required",
    ready: "ready_for_review",
    review: "ready_for_review",
    submitted: "pending_approval",
    needs_approval: "pending_approval"
  };
  const candidate = aliases[normalized] || (normalized as PageChatStructuredSetupStatus);
  return ["applied", "draft", "failed", "handoff_required", "pending_approval", "ready_for_review"].includes(candidate)
    ? candidate
    : "draft";
};

export const defaultStructuredSetupActions = (status?: unknown): PageChatStructuredSetupAction[] => {
  const normalizedStatus = normalizeStructuredSetupStatus(status);

  if (normalizedStatus === "applied") {
    return [
      { id: "ready_for_review", label: "Review again", status: "ready_for_review", kind: "review" },
      { id: "handoff_required", label: "Send to handoff", status: "handoff_required", kind: "handoff" }
    ];
  }

  if (normalizedStatus === "ready_for_review") {
    return [
      { id: "pending_approval", label: "Needs approval", status: "pending_approval", kind: "review" },
      { id: "handoff_required", label: "Send to handoff", status: "handoff_required", kind: "handoff" }
    ];
  }

  if (normalizedStatus === "pending_approval") {
    return [
      { id: "ready_for_review", label: "Back to review", status: "ready_for_review", kind: "review" },
      { id: "handoff_required", label: "Send to handoff", status: "handoff_required", kind: "handoff" }
    ];
  }

  if (normalizedStatus === "handoff_required") {
    return [
      { id: "open_editor", label: "Open editor", kind: "open_editor", href: STRUCTURED_SETUP_WORKFLOW_EDITOR_HREF },
      { id: "ready_for_review", label: "Back to review", status: "ready_for_review", kind: "review" }
    ];
  }

  if (normalizedStatus === "failed") {
    return [
      { id: "draft", label: "Retry draft", status: "draft", kind: "review" },
      { id: "ready_for_review", label: "Retry review", status: "ready_for_review", kind: "review" }
    ];
  }

  return [
    { id: "ready_for_review", label: "Mark ready for review", status: "ready_for_review", kind: "review" },
    { id: "pending_approval", label: "Needs approval", status: "pending_approval", kind: "review" },
    { id: "handoff_required", label: "Send to handoff", status: "handoff_required", kind: "handoff" }
  ];
};

const normalizeStructuredSetupSection = (value: unknown): PageChatStructuredSetupSection | null => {
  if (typeof value === "string") {
    return { title: "Details", body: value.trim() };
  }
  if (!isPlainObject(value)) return null;

  const section: PageChatStructuredSetupSection = {
    title: toText(value.title, "Details")
  };
  if (value.body !== undefined && value.body !== null) {
    section.body = toText(value.body);
  }
  if (Array.isArray(value.items)) {
    section.items = value.items.map((item) => toText(item)).filter(Boolean);
  }
  if (!section.items && value.items !== undefined && value.items !== null) {
    const singleItem = toText(value.items);
    if (singleItem) {
      section.items = [singleItem];
    }
  }
  return section;
};

const normalizeStructuredSetupAction = (value: unknown): PageChatStructuredSetupAction | null => {
  if (typeof value === "string") {
    return {
      id: value,
      label: value.replace(/_/g, " ").trim() || value
    };
  }
  if (!isPlainObject(value)) return null;

  const action: PageChatStructuredSetupAction = {
    id: toText(value.id || value.status || value.label, "action"),
    label: toText(value.label, toText(value.id || value.status || value.label, "Action"))
  };
  if (value.kind !== undefined && value.kind !== null) {
    const actionKind = toText(value.kind)
      .toLowerCase()
      .replace(/[-\s]+/g, "_");
    if (actionKind) {
      action.kind = actionKind;
    }
  }
  if (value.description !== undefined && value.description !== null) {
    action.description = toText(value.description);
  }
  if (action.kind === "open_editor") {
    action.href = STRUCTURED_SETUP_WORKFLOW_EDITOR_HREF;
  } else if (value.status !== undefined && value.status !== null) {
    action.status = normalizeStructuredSetupStatus(value.status);
  } else if (action.kind === "apply") {
    action.status = "applied";
  } else if (action.kind === "handoff") {
    action.status = "handoff_required";
  } else if (action.kind === "review") {
    action.status = "ready_for_review";
  }
  if (action.kind !== "open_editor") {
    const href = normalizeActionHref(value.href);
    if (href) {
      action.href = href;
    }
  }
  return action;
};

const mergeStructuredSetupSources = (value: Record<string, any>) => {
  const nested = value.structured_setup;
  if (!isPlainObject(nested)) return { ...value };

  const merged: Record<string, any> = { ...nested };
  Object.entries(value).forEach(([key, currentValue]) => {
    if (key === "structured_setup") return;
    if (!(key in merged)) {
      merged[key] = currentValue;
    }
  });
  return merged;
};

export const coerceStructuredSetupPayload = (value: unknown): PageChatStructuredSetupPayload | null => {
  if (!isPlainObject(value)) return null;

  const source = mergeStructuredSetupSources(value);
  const hasStructuredMarkers =
    source.kind === STRUCTURED_SETUP_KIND ||
    source.item_type === STRUCTURED_SETUP_ITEM_TYPE ||
    isPlainObject(source.structured_payload) ||
    source.intent_summary !== undefined ||
    source.recommended_action !== undefined ||
    isPlainObject(value.structured_setup);

  if (!hasStructuredMarkers) return null;

  const payload: PageChatStructuredSetupPayload = {
    ...source,
    kind: STRUCTURED_SETUP_KIND,
    item_type: source.item_type || STRUCTURED_SETUP_ITEM_TYPE,
    version: toText(source.version, "1"),
    domain: toText(source.domain, "workflow"),
    title: toText(source.title || source.intent_summary, "Structured setup proposal"),
    intent_summary: toText(source.intent_summary || source.summary, toText(source.title, "Structured setup proposal")),
    recommended_action: toText(source.recommended_action, "Review the proposal"),
    risk_tier: toText(source.risk_tier, "medium"),
    status: normalizeStructuredSetupStatus(source.status),
    prerequisites: [],
    sections: [],
    structured_payload: isPlainObject(source.structured_payload) ? source.structured_payload : {},
    provenance: isPlainObject(source.provenance) ? source.provenance : {},
    allowed_actions: [],
    summary: toText(source.summary, toText(source.intent_summary, "Structured setup proposal")),
    message: toText(source.message, toText(source.intent_summary, "Structured setup proposal")),
    source: toText(source.source, "pagechat")
  };

  if (Array.isArray(source.prerequisites)) {
    payload.prerequisites = source.prerequisites.map((item) => toText(item)).filter(Boolean);
  }

  if (Array.isArray(source.sections)) {
    payload.sections = source.sections
      .map(normalizeStructuredSetupSection)
      .filter(Boolean) as PageChatStructuredSetupSection[];
  }

  if (Array.isArray(source.allowed_actions)) {
    payload.allowed_actions = source.allowed_actions
      .map(normalizeStructuredSetupAction)
      .filter(Boolean) as PageChatStructuredSetupAction[];
  }
  if (!payload.allowed_actions.length) {
    payload.allowed_actions = defaultStructuredSetupActions(payload.status);
  }

  return payload;
};

export const getStructuredSetupStatusLabel = (status?: unknown) =>
  normalizeStructuredSetupStatus(status)
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

export const getStructuredSetupStatusColor = (status?: unknown) => {
  const normalized = normalizeStructuredSetupStatus(status);
  if (normalized === "applied") return "success";
  if (normalized === "ready_for_review") return "info";
  if (normalized === "pending_approval") return "warning";
  if (normalized === "handoff_required") return "secondary";
  if (normalized === "failed") return "error";
  return "default";
};

export const isStructuredSetupPayload = (value: unknown): value is PageChatStructuredSetupPayload =>
  Boolean(coerceStructuredSetupPayload(value));
